import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from "@/components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  FolderPlus,
  MoreVertical,
  Edit,
  Trash2,
  Folder,
  Users,
  User
} from "lucide-react";
import { candidateFoldersAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import DeleteFolderConfirmDialog from "./DeleteFolderConfirmDialog";

export default function FolderManagementModal({
  isOpen,
  onClose,
  onCreateFolder,
  onEditFolder,
  onFolderCreatedOrEdited
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [createdByFilter, setCreatedByFilter] = useState("all");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, folder: null });
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const { toast } = useToast();
  const limit = 8; // Show 8 folders per page in modal

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['folders-management-modal', { page, limit, searchTerm, createdByFilter, refetchTrigger }],
    queryFn: () => candidateFoldersAPI.getAll({
      page,
      limit,
      search: searchTerm,
      createdBy: createdByFilter
    }),
    enabled: isOpen,
    keepPreviousData: true, // keeps old data while fetching new
    refetchOnMount: true, // Always refetch when modal opens
  });

  // Refetch folders when modal opens and reset to page 1
  useEffect(() => {
    if (isOpen) {
      setPage(1); // Reset to first page when modal opens
      setSearchTerm(""); // Clear search
      refetch();
    }
  }, [isOpen, refetch]);

  // Function to trigger refetch from outside
  const triggerRefetch = () => {
    setPage(1); // Reset to page 1
    setRefetchTrigger(prev => prev + 1); // Change trigger to force refetch
    if (onFolderCreatedOrEdited) {
      onFolderCreatedOrEdited();
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
  };

  const handleDeleteFolder = async (folderId, confirmation) => {
    try {
      await candidateFoldersAPI.delete(folderId, confirmation);
      toast({
        title: "Folder deleted",
        description: "The folder has been deleted successfully and candidates moved to Others",
      });
      setDeleteDialog({ open: false, folder: null });
      triggerRefetch(); // Refetch folders in modal after deletion
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete folder",
        variant: "destructive",
      });
    }
  };

  // Function to handle folder creation - pass refetch callback
  const handleCreateFolderSuccess = () => {
    onCreateFolder(triggerRefetch);
  };

  // Function to handle folder edit - pass refetch callback
  const handleEditFolderSuccess = (folder) => {
    onEditFolder(folder, triggerRefetch);
  };

  const folders = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder className="h-5 w-5" />
              Folder Management
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Header with filters and create button */}
            <div className="flex items-center justify-between">
              <Button onClick={handleCreateFolderSuccess} size="sm">
                <FolderPlus className="w-4 h-4 mr-2" />
                Create Folder
              </Button>

              <div className="flex items-center gap-2">
                <Select value={createdByFilter} onValueChange={(value) => { setCreatedByFilter(value); setPage(1); }}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Folders</SelectItem>
                    <SelectItem value="me">My Folders</SelectItem>
                    <SelectItem value="company">Company Folders</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex items-center space-x-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Search folders..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Button type="submit" size="sm">Search</Button>
            </form>

            {/* Folder Grid */}
            <div className="min-h-[400px] max-h-[50vh] overflow-y-auto">
              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
                </div>
              ) : folders.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-4">
                  {folders.map((folder) => (
                    <div
                      key={folder._id}
                      className="relative group border rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <div className="relative">
                          <Folder
                            className="h-12 w-12"
                            style={{ color: folder.color }}
                          />
                          {folder.candidateCount > 0 && (
                            <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                              {folder.candidateCount}
                            </div>
                          )}
                        </div>

                        <div className="text-center">
                          <h3 className="font-medium text-sm truncate w-full" title={folder.name}>
                            {folder.name}
                          </h3>
                          {folder.description && (
                            <p className="text-xs text-gray-500 truncate" title={folder.description}>
                              {folder.description}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center text-xs text-gray-400">
                          {folder.createdBy?.name === folder.createdBy?.name ? (
                            <User className="h-3 w-3 mr-1" />
                          ) : (
                            <Users className="h-3 w-3 mr-1" />
                          )}
                          <span className="truncate">{folder.createdBy?.name || 'Unknown'}</span>
                        </div>

                        <div className="text-xs text-gray-400">
                          {format(new Date(folder.createdAt), "MMM dd, yyyy")}
                        </div>
                      </div>

                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6">
                              <MoreVertical className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditFolderSuccess(folder)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeleteDialog({ open: true, folder })}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-12 text-gray-500">
                  <Folder className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No folders found</p>
                  <p className="text-sm">Create your first folder to organize candidates</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white py-3 flex justify-center border-t">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setPage((p) => Math.max(p - 1, 1))}
                        disabled={page === 1}
                      />
                    </PaginationItem>

                    {[...Array(pagination.totalPages).keys()].map((i) => {
                      const pageNumber = i + 1;
                      return (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink
                            isActive={page === pageNumber}
                            onClick={() => setPage(pageNumber)}
                            className={`px-3 py-1 rounded-md transition-colors ${page === pageNumber
                                ? "bg-blue-500 text-white"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                              }`}
                          >
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}

                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                        disabled={page === pagination.totalPages}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <DeleteFolderConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, folder: null })}
        folder={deleteDialog.folder}
        onConfirm={handleDeleteFolder}
      />
    </>
  );
}